import { Injectable } from "@angular/core";
import { ToastController } from "@ionic/angular";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { take } from "rxjs/operators";
import { ApiService } from "src/app/services/api/api.service";
import { create_event, teaming_up_group } from "./model";
import moment from 'moment';
import 'moment-timezone';
import { PandaConnectBLL } from "../../panda-connect/panda-connect.bll";
import { FieldSchedulesBLL } from "../field-schedules.bll";
import { FieldScheduleReportBLL } from "../../field-schedule-report/field-schedule-report.bll";

@Injectable({
  providedIn: "root"
})
export class AwayADActionBLL {

  private isLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  readonly isLoading$: Observable<boolean> = this.isLoading.asObservable();

  private schools: BehaviorSubject<any> = new BehaviorSubject<any>([]);
  readonly schools$: Observable<any> = this.schools.asObservable();

  private ad_contacts: BehaviorSubject<any> = new BehaviorSubject<any>([]);
  readonly ad_contacts$: Observable<any> = this.ad_contacts.asObservable();

  private referee_contacts: BehaviorSubject<any> = new BehaviorSubject<any>([]);
  readonly referee_contacts$: Observable<any> = this.referee_contacts.asObservable();

  private field_facilities: BehaviorSubject<any> = new BehaviorSubject<any>([]);
  readonly field_facilities$: Observable<any> = this.field_facilities.asObservable();


  private bus_contacts: BehaviorSubject<any> = new BehaviorSubject<any>([]);
  readonly bus_contacts$: Observable<any> = this.bus_contacts.asObservable();

  private leagues: BehaviorSubject<any> = new BehaviorSubject<any>([]);
  readonly leagues$: Observable<any> = this.leagues.asObservable();

  // Separate from isLoading: most of the Manage Activity form is hidden behind
  // *ngIf="!(isLoading$ | async)", so the leagues fetch must not blank the panel.
  private leaguesLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  readonly leaguesLoading$: Observable<boolean> = this.leaguesLoading.asObservable();

  private isTeamsLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  readonly isTeamsLoading$: Observable<boolean> = this.isTeamsLoading.asObservable();

  private event_created: BehaviorSubject<string> = new BehaviorSubject<string>('');
  readonly event_created$: Observable<string> = this.event_created.asObservable();

  private event_updated: BehaviorSubject<string> = new BehaviorSubject<string>('');
  readonly event_updated$: Observable<string> = this.event_updated.asObservable();

  private event: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  readonly event$: Observable<any> = this.event.asObservable();

  private teams: BehaviorSubject<teaming_up_group[]> = new BehaviorSubject<teaming_up_group[]>([]);
  readonly teams$: Observable<teaming_up_group[]> = this.teams.asObservable();

  private opposing_teams: BehaviorSubject<teaming_up_group[]> = new BehaviorSubject<teaming_up_group[]>([]);
  readonly opposing_teams$: Observable<teaming_up_group[]> = this.opposing_teams.asObservable();

  private show_addMenu: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  readonly show_addMenu$: Observable<boolean> = this.show_addMenu.asObservable();

  private group_activity: BehaviorSubject<any> = new BehaviorSubject<any>({});
  readonly group_activity$: Observable<any> = this.group_activity.asObservable();

  selectedFacility$: Subject<any> = new Subject<any>();
  selectedBuses$: Subject<any> = new Subject<any>();
  selectedAD$: Subject<any> = new Subject<any>();
  selectedReferee$: Subject<any> = new Subject<any>();



  constructor(
    private api: ApiService,
    private toast: ToastController,
    private connectBll: PandaConnectBLL,
    private fieldBll: FieldSchedulesBLL,
    private fieldScheduleReportBLL: FieldScheduleReportBLL

  ) { }

  toggle_add_menu(show: boolean) {
    if (show) {
      // get apis
      this.connectBll.getSports();
      this.get_schools();
    }
    this.show_addMenu.next(show);
  }

  // Feeds the "attach an existing league schedule" picker. Scoped to the field event so the
  // API returns the leagues of the AD who sent the invite, not this (opposing) AD's own.
  // The AD's own assigned leagues — the same list for every event they were invited to,
  // so the previous result is kept on screen while a refresh runs (no per-event clear)
  public get_assigned_leagues() {
    this.leaguesLoading.next(true);

    let url = `league?type=get_leagues_assigned`;

    this.api.get(url).pipe(take(1)).subscribe((response: any) => {
      this.leagues.next(response.assigned_leagues || response.leagues || []);
      this.leaguesLoading.next(false);
    }, (err) => {
      this.leaguesLoading.next(false);
      console.error('Error fetching leagues:', err);
    });
  }

  reset_observables() {
    this.schools.next([]);
  }

  get_schools() {
    this.isLoading.next(true);
    let url = `field-events`;

    this.api.get(url).pipe(take(1)).subscribe((res: any) => {
      this.isLoading.next(false);
      // let school_and_clubs = [...res.my_experience, ...res.my_clubs];
      this.schools.next(res.my_clubs);
      this.ad_contacts.next(res.ad_contacts);
      this.referee_contacts.next(res.referee_contacts);
      this.field_facilities.next(res.field_facilities);
      this.bus_contacts.next(res.bus_contacts);
    }, (err) => {
      this.isLoading.next(false);
    })

  }

  get_facilities(id) {
    this.isLoading.next(true);
    let url = `field-events`;
    this.api.get(url).pipe(take(1)).subscribe((res: any) => {
      this.isLoading.next(false);
      this.field_facilities.next(res.field_facilities);
      res.field_facilities.filter((item) => {
        if (item.id == id) {
          this.selectedFacility$.next(item);
        }
      });
    }, (err) => {
      this.isLoading.next(false);
    });
  }

  get_bus_contacts(id) {
    this.isLoading.next(true);
    let url = `field-events`;
    this.api.get(url).pipe(take(1)).subscribe((res: any) => {
      this.isLoading.next(false);
      this.bus_contacts.next(res.bus_contacts);
      res.bus_contacts.filter((item) => {
        if (item.id == id) {
          this.selectedBuses$.next(item);
        }
      });
    }, (err) => {
      this.isLoading.next(false);
    });
  }


  get_ad_contacts(id) {
    this.isLoading.next(true);
    let url = `field-events`;
    this.api.get(url).pipe(take(1)).subscribe((res: any) => {
      this.isLoading.next(false);
      this.ad_contacts.next(res.ad_contacts);
      res.ad_contacts.filter((item) => {
        if (item.id == id) {
          this.selectedAD$.next(item);
        }
      });
    }, (err) => {
      this.isLoading.next(false);
    });
  }


  get_referee_contacts(id) {
    this.isLoading.next(true);
    let url = `field-events`;
    this.api.get(url).pipe(take(1)).subscribe((res: any) => {
      this.isLoading.next(false);
      this.referee_contacts.next(res.referee_contacts);
      res.referee_contacts.filter((item) => {
        if (item.id == id) {
          this.selectedReferee$.next(item);
        }
      });
    }, (err) => {
      this.isLoading.next(false);
    });
  }


  update_field_event(event: create_event) {
    
    this.isLoading.next(true);
    let url = `field-events?type=patch_opposing_ad_field_event&timezone=${moment.tz.guess()}`;

    this.api.patch(url, event).pipe(take(1)).subscribe(async (res: any) => {
      this.isLoading.next(false);
      const t = await this.toast.create({
        header: 'Field Event Updated',
        message: `Field Event has been updated successfully.`,
        duration: 5000,
        color: 'success'
      });
      await t.present();
      this.event_updated.next("updated");
      this.toggle_add_menu(false);
      this.fieldBll.get_all_sessions(moment().format('YYYY-MM-DD'));
      this.fieldBll.get_todays_sessions();
      this.fieldScheduleReportBLL.get_sessions();

    }, async (err) => {
      this.isLoading.next(false);
      this.toggle_add_menu(false);
    });
  }

  reset_event_created() {
    this.event_created.next('');
  }

  reset_event_update() {
    this.event_updated.next('');
  }

  reset_teams_observable() {
    this.teams.next([]);
  }

}